import { FileText, IdCard, Route as RouteIcon, UserPlus } from 'lucide-react';

import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import type { DriverSummary } from '@/types/transport';
import { toDateLabel } from '../constants';
import { QuickActionGrid, RailCard, type QuickAction } from './TransportSideRail';

export interface DriversSideRailProps {
  summary: DriverSummary;
  actions: QuickAction[];
  onViewStatusReport?: () => void;
  onViewLicenceReport?: () => void;
}

/**
 * The Drivers tab's rail: how the roll divides, and whether its licences hold.
 *
 * Two rings for the same reason the fleet has two — where people are today, and
 * whether they are legally able to be there. A licence that lapsed is not a
 * driver on leave, and one ring could not say both.
 */
export function DriversSideRail({
  summary,
  actions,
  onViewStatusReport,
  onViewLicenceReport,
}: DriversSideRailProps) {
  const statusSlices: DonutSlice[] = [
    {
      key: 'onDuty',
      label: 'On duty',
      value: summary.onDuty,
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'onLeave',
      label: 'On leave',
      value: summary.onLeave,
      stroke: 'stroke-amber-500',
      dot: 'bg-amber-500',
    },
    {
      key: 'inactive',
      label: 'Inactive',
      value: summary.inactive,
      stroke: 'stroke-rose-500',
      dot: 'bg-rose-500',
    },
  ];

  const licenceSlices: DonutSlice[] = [
    {
      key: 'valid',
      label: 'Valid',
      value: summary.valid,
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'expired',
      label: 'Expired',
      value: summary.expired,
      stroke: 'stroke-rose-500',
      dot: 'bg-rose-500',
    },
    {
      key: 'expiringSoon',
      label: 'Expiring soon',
      value: summary.expiringSoon,
      stroke: 'stroke-amber-500',
      dot: 'bg-amber-500',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <RailCard
        title="Driver status"
        action={onViewStatusReport && { label: 'View report', onSelect: onViewStatusReport }}
      >
        <DonutChart
          slices={statusSlices}
          total={summary.totalDrivers}
          caption="Total drivers"
          layout="responsive"
        />
      </RailCard>

      <RailCard
        title="Licence overview"
        action={onViewLicenceReport && { label: 'View report', onSelect: onViewLicenceReport }}
      >
        <DonutChart
          slices={licenceSlices}
          total={summary.valid + summary.expired + summary.expiringSoon}
          caption="Licences"
          layout="responsive"
        />

        {/* Who to chase first. A percentage says the roll is fine; a name and a
            date says what to do about the one that is not. */}
        {summary.nextExpiry && (
          <dl className="mt-3 flex items-baseline justify-between gap-3 border-t border-line pt-3">
            <dt className="lbl shrink-0 text-ink-3">Next expiry</dt>
            <dd className="min-w-0 text-right">
              <span className="block truncate text-sm font-bold text-ink">
                {toDateLabel(summary.nextExpiry.on)}
              </span>
              <span className="block truncate text-xs font-semibold text-ink-3">
                {summary.nextExpiry.driverName}
              </span>
            </dd>
          </dl>
        )}
      </RailCard>

      <RailCard title="Quick actions">
        <QuickActionGrid actions={actions} />
      </RailCard>
    </div>
  );
}

/** The four things the office does to a driver. */
export const DRIVER_ACTION_ICONS = {
  addDriver: UserPlus,
  driverReport: FileText,
  licenceReport: IdCard,
  assignRoute: RouteIcon,
};
